(() => {
  const initialize = () => {
    const roots = Array.from(document.querySelectorAll('[data-steep-b-root]'));

    roots.forEach((root) => {
      if (root.dataset.trialReady === 'true') return;
      root.dataset.trialReady = 'true';

      const variantId = Number(root.dataset.variantId || 0);
      const price = Number(root.dataset.priceValue || 0);
      const offer = root.dataset.offer || '';
      const landingPage = root.dataset.landingPage || '';
      const sku = root.dataset.trialSku || '';
      const packLabel = root.dataset.packLabel || 'trial pack';
      const cartProperty = root.dataset.cartProperty || packLabel;
      const buyButtons = Array.from(root.querySelectorAll('[data-trial-buy]'));
      const statusNodes = Array.from(root.querySelectorAll('[data-trial-status]'));
      const sticky = root.querySelector('[data-trial-sticky]');
      const heroButton = root.querySelector('[data-trial-primary]');
      let purchasePending = false;
      let checkoutPending = false;

      const track = (event, details = {}) => {
        const payload = {
          event,
          experiment_id: 'steep-lp-ab-v1',
          experiment_variant: 'B',
          landing_page: landingPage,
          offer,
          variant_id: variantId,
          sku,
          value: price,
          currency: 'USD',
          ...details
        };
        window.__steepExperimentEvents = window.__steepExperimentEvents || [];
        window.__steepExperimentEvents.push(payload);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        window.dispatchEvent(new CustomEvent(event, { detail: payload }));
        try {
          window.Shopify?.analytics?.publish?.(event, payload);
        } catch (error) {
          console.debug('[Steep LP test B] Shopify analytics publish skipped', error);
        }
      };

      const setStatus = (message = '') => statusNodes.forEach((node) => { node.textContent = message; });
      const setButtonState = (disabled, label) => buyButtons.forEach((button) => {
        if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent.trim();
        button.disabled = disabled;
        button.textContent = label || button.dataset.originalLabel;
      });

      // Back/forward cache preserves the checkout lock; reset only after requests finish.
      window.addEventListener('pageshow', () => {
        if (purchasePending && !checkoutPending) return;
        purchasePending = false;
        checkoutPending = false;
        setButtonState(false);
        setStatus();
      });

      const changeCartLine = async (key, quantity) => {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'We couldn’t update your cart.');
        return payload;
      };

      const normalizeOfferInCart = async () => {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || 'We couldn’t check your cart.');

        const offerLines = cart.items.filter((item) => Number(item.variant_id) === variantId);
        if (!offerLines.length) return false;

        for (const [index, item] of offerLines.entries()) {
          const allowedQuantity = index === 0 ? 1 : 0;
          if (Number(item.quantity) !== allowedQuantity) await changeCartLine(item.key, allowedQuantity);
        }
        return true;
      };

      const buy = async () => {
        if (purchasePending) return;
        if (!variantId) {
          setStatus('This offer is still being prepared. Please check back shortly.');
          return;
        }

        purchasePending = true;
        setStatus(`Adding your ${packLabel}…`);
        setButtonState(true, 'ADDING…');

        try {
          const alreadyInCart = await normalizeOfferInCart();
          if (!alreadyInCart) {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{
                  id: variantId,
                  quantity: 1,
                  properties: {
                    '_Trial offer': cartProperty,
                    '_Landing page': landingPage,
                    '_Experiment': 'steep-lp-ab-v1:B'
                  }
                }]
              })
            });
            const payload = await response.json();
            if (!response.ok) throw new Error(payload.description || payload.message || 'We couldn’t add the offer.');
            track('steep_trial_add_to_cart');
          }

          setStatus('Added. Taking you to secure checkout…');
          setButtonState(true, 'GOING TO CHECKOUT…');
          track('steep_trial_initiate_checkout');
          checkoutPending = true;
          window.location.assign('/checkout');
        } catch (error) {
          purchasePending = false;
          checkoutPending = false;
          setButtonState(false);
          setStatus(error?.message || 'Something went wrong. Please try again.');
        }
      };

      buyButtons.forEach((button) => button.addEventListener('click', buy));

      if (sticky && heroButton) {
        let stickyFrame = 0;
        const updateSticky = () => {
          stickyFrame = 0;
          const shouldShow = heroButton.getBoundingClientRect().bottom < 0;
          sticky.classList.toggle('is-visible', shouldShow);
          sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        };
        const requestStickyUpdate = () => {
          if (stickyFrame) return;
          stickyFrame = requestAnimationFrame(updateSticky);
        };
        window.addEventListener('scroll', requestStickyUpdate, { passive: true });
        window.addEventListener('resize', requestStickyUpdate, { passive: true });
        updateSticky();
      }

      const revealTargets = Array.from(root.querySelectorAll('[data-trial-reveal]'));
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        revealTargets.forEach((target) => target.classList.add('is-revealed'));
      } else {
        const revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -8%', threshold: 0.08 });
        revealTargets.forEach((target) => revealObserver.observe(target));
      }

      const suppressKlaviyo = () => {
        document.querySelectorAll('[aria-label="POPUP Form"], [class*="kl-teaser-"]').forEach((node) => {
          if (!(node instanceof HTMLElement) || node.closest('[data-steep-b-root]')) return;
          node.style.setProperty('display', 'none', 'important');
          node.setAttribute('aria-hidden', 'true');
        });
      };
      suppressKlaviyo();
      const klaviyoObserver = new MutationObserver(suppressKlaviyo);
      klaviyoObserver.observe(document.body, { childList: true, subtree: true });

      track('steep_trial_view');
      console.info('[Steep LP test B] Ready', { landingPage, variantId, offer });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
